import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  INJECTOR,
  input,
  Input,
  output,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Bewe, Bewelg } from '../../types/bewegung';
import { block, handleRequestError, MengenEingabeComponent } from '@softline/application';
import { MessageBarService, modalFn } from '@softline/ui-core';
import { SOFTLINE_CONFIG_WARENUEBERNAHME } from '../../warenuebernahme.shared';
import { Lagerplatz, Palette, PalletItemListModalComponent } from '@softapps/lager/core';
import { FieldOkModalService } from '@softline/dynamic';
import { LagerplatzService } from '../../services/lagerplatz-service';
import { SelectLagerplatzModalComponent } from '../../dialogs/select-lagerplatz-modal/select-lagerplatz-modal.component';
import { PalletItem } from '../../types/pallet-item';
import { PalletItemService } from '../../services/pallet-item-service';

import { IconComponent } from '@softline/ui-core';
import { IsDefinedNotEmptyPipe } from '@softline/ui-core';
import { ToolTipDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-bewegung-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MengenEingabeComponent,
    IconComponent,
    IsDefinedNotEmptyPipe,
    ToolTipDirective,
    TranslatePipe,
  ],
  templateUrl: './bewegung-list.component.html',
  styleUrls: ['./bewegung-list.component.css'],
  providers: [LagerplatzService],
})
export class BewegungListComponent {
  readonly config = inject(SOFTLINE_CONFIG_WARENUEBERNAHME);
  readonly injector = inject(INJECTOR);
  readonly fieldOkService = inject(FieldOkModalService);
  readonly modal = modalFn();
  readonly service = inject(LagerplatzService);
  readonly palletService = inject(PalletItemService);
  readonly messageBarService = inject(MessageBarService);

  @Input() isBestellung = false;
  @Input() bewegungen: Bewe[] = [];
  readonly palletItems = input<PalletItem[]>();

  @Output() statusChange = new EventEmitter<Bewe>();
  beweLgStatusChange = output<{ bewe: Bewe; bewelg: Bewelg }>();

  beweLgLagerplatzChange = output<{
    bewe: Bewe;
    bewelg: Bewelg;
    lagerplatz: Lagerplatz;
  }>();

  bewelgMengeChange = output<{ bewe: Bewe; bewelg: Bewelg; menge: number }>();
  @Output() mengeChange = new EventEmitter<{ bewe: Bewe; menge: number }>();
  @Output() mengenEingabeOeffnen = new EventEmitter<Bewe>();
  @Output() actionClick = new EventEmitter<Bewe>();

  lagerplatzChange = output<{ bewe: Bewe; lagerplatz: Lagerplatz }>();

  hasReklamationPermission = input(false);

  resetLagerplatz = signal<Lagerplatz | null>(null, {
    equal: () => false,
  });

  trackByFn(_: number, bewe: Bewe): number {
    return bewe.id;
  }

  hasSubItems(bewe: Bewe) {
    if (
      bewe.artikelMenge.artikel.kzartikelart === 'DIF' ||
      bewe.artikelMenge.artikel.kzartikelart === 'SDIF'
    )
      return true;

    return (bewe?.anzahlPalettenSoll ?? 0) > 0;
  }

  protected readonly Number = Number;

  async openCustomSelect(bewe: Bewe, bewelg?: Bewelg): Promise<void> {
    try {
      const result = await block(
        () =>
          this.loadLagerplaetze(
            bewe,
            bewe?.idlagerstamm,
            bewe?.artikelMenge?.einheit_lg?.id
          ),
        this.injector
      );

      if (!result || result.length === 0) {
        const fokResult = await this.openFielOkLagerplaetze(bewe?.idlagerstamm);
        if (!fokResult || typeof fokResult === 'string') return;

        if (bewelg) {
          console.log('bewelg change emit', bewelg);
          this.beweLgLagerplatzChange.emit({
            bewe,
            lagerplatz: fokResult as Lagerplatz,
            bewelg,
          });
        } else {
          this.lagerplatzChange.emit({
            bewe,
            lagerplatz: fokResult as Lagerplatz,
          });
        }

        return;
      }

      let selected = await this.modal({
        component: SelectLagerplatzModalComponent,
        separateContentSection: true,
        title: 'Lagerplätze mit Menge',
        subtitle: 'Bitte Lagerplatz auswählen',
        data: {
          lagerplaetzeInhalt: result,
        },
      });

      if (!selected || selected === 'DISMISSED') return;

      if (selected === 'ALL_STORAGE_LOCATIONS')
        selected = await this.openFielOkLagerplaetze(bewe?.idlagerstamm);

      if (!selected) return;

      console.log('bewelg change emit 1', bewelg);

      if (bewelg) {
        console.log('bewelg change emit', bewelg);
        this.beweLgLagerplatzChange.emit({
          bewe,
          lagerplatz: selected as Lagerplatz,
          bewelg,
        });
      } else {
        this.lagerplatzChange.emit({
          bewe,
          lagerplatz: selected as Lagerplatz,
        });
      }
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  private async openFielOkLagerplaetze(idlagerstamm?: number): Promise<any> {
    try {
      const result = await this.fieldOkService.open<Lagerplatz>(
        'lagerplatz',
        idlagerstamm
          ? {
              idlagerstamm,
            }
          : {}
      );
      if (!result || result === 'DISMISSED' || result === 'CANCELED') return null;
      return result;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  private async loadLagerplaetze(
    bewe: Bewe,
    idlagerstamm?: string | number,
    idarteh?: number
  ): Promise<any[] | null> {
    try {
      return await this.service.loadLagerplaetzeForProduct(bewe.artikelMenge.artikel.id, {
        idlagerstamm,
        idarteh,
      });
    } catch (e) {
      handleRequestError(e, this.injector, { show: false });
      return null;
    }
  }

  async openPalletItemsList(item: PalletItem): Promise<void> {
    await this.modal({
      title: 'Mischpaletten Info',
      component: PalletItemListModalComponent,
      separateContentSection: true,
      data: {
        items: item.positionen.map((o) => o.artikelMenge) ?? [],
        pallet: {
          ...item?.palette,
          lagerplatz: item?.lagerplatz,
        } as Palette,
      },
    });
  }

  async toggleCompleteOpen(item: PalletItem): Promise<void> {
    try {
      await block(async () => {
        if (item.status === 'E') await this.palletService.open(item);
        else await this.palletService.complete(item);
      }, this.injector);

      this.messageBarService.success(
        item.status === 'E'
          ? '#WWS_WARENUEBERNAHME.DETAIL.MESSAGES.MESSAGE_POSITION_GEOEFFNET'
          : '#WWS_WARENUEBERNAHME.DETAIL.MESSAGES.MESSAGE_POSITION_ERLEDIGT'
      );
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }
}
